import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ModelWarehouseInventoryState,
  useLazyGetInventoriesSearchResultQuery,
  useSearchInventoriesMutation,
  type ModelInventoriesSearchResult,
  type ModelWarehouseInventory,
} from '@app/api/WarehouseInventories';
import { EDIT_INVENTORY_PATH, INVENTORY_PATH } from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import {
  initialFilters,
  getInventoryColumns,
} from '@app/pages/Warehouses/Inventory/data';
import type {
  InventoryFilters,
  PaginationInterface,
} from '@app/pages/Warehouses/Inventory/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useGetWarehousesQuery } from '@app/services/rtkQuery/Warehouses/api';
import { useErrorHandler } from '@app/shared/hooks';

export const useInventory = () => {
  const navigate = useNavigate();
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const {
    userAccessRights: {
      Warehouse: { IsInventoryEdit, IsInventoryCompleting, IsInventoryReports },
    },
  } = useNewUserAccessRights();
  const { showNotification } = useNotification();
  const [filters, setFilters] = useState<InventoryFilters>(
    () => initialFilters
  );
  const searchIdRef = useRef<number | null>(null);
  const lastBranchRef = useRef(ActiveBranchId);
  const { ItemsPerPage, Page } = filters;
  const [inventoriesSearchResult, setInventoriesSearchResult] =
    useState<ModelInventoriesSearchResult | null>(null);
  const total = useMemo(() => {
    if (!inventoriesSearchResult) {
      return 0;
    }

    const { CountInventories } = inventoriesSearchResult;
    return CountInventories ?? 0;
  }, [inventoriesSearchResult]);

  const range = useMemo(() => {
    const from = (Page - 1) * ItemsPerPage + 1;
    const to = Math.min(Page * ItemsPerPage, total);
    return { from, to };
  }, [Page, ItemsPerPage, total]);

  const tableData: ModelWarehouseInventory[] = useMemo(() => {
    if (!inventoriesSearchResult) {
      return [];
    }

    const { Inventories } = inventoriesSearchResult;
    return Inventories ?? [];
  }, [inventoriesSearchResult]);

  const {
    data: warehouses,
    isLoading: isLoadingWarehouses,
    isError: isErrorWarehouses,
    error: errorWarehouses,
  } = useGetWarehousesQuery({ ActiveBranchId }, { skip: !ActiveBranchId });

  const [
    searchInventories,
    {
      isLoading: isLoadingSearchInventories,
      isError: isErrorSearchInventories,
      error: errorSearchInventories,
    },
  ] = useSearchInventoriesMutation();

  const [
    getInventoriesSearchResult,
    {
      isLoading: isLoadingGetInventoriesSearchResult,
      isError: isErrorGetInventoriesSearchResult,
      error: errorGetInventoriesSearchResult,
    },
  ] = useLazyGetInventoriesSearchResultQuery();

  useErrorHandler([
    { flag: isErrorSearchInventories, error: errorSearchInventories },
    {
      flag: isErrorGetInventoriesSearchResult,
      error: errorGetInventoriesSearchResult,
    },
    { flag: isErrorWarehouses, error: errorWarehouses },
  ]);

  const getInventoriesSearchResultHandler = useCallback(
    async ({ page, pageSize }: PaginationInterface) => {
      const searchId = searchIdRef.current;

      if (!searchId) {
        showNotification(
          'Не удалось найти идентификатор поиска',
          'frontWarning'
        );
        return;
      }
      try {
        const response = await getInventoriesSearchResult({
          searchId,
          pageN: page,
          itemsPerPage: pageSize,
          ActiveBranchId,
        }).unwrap();

        setInventoriesSearchResult(response);
      } catch (error) {
        showNotification('Ошибка при получении результатов поиска', 'warning');
      }
    },
    [getInventoriesSearchResult, ActiveBranchId, showNotification]
  );

  const handlePaginationChange = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      Page: page,
      ItemsPerPage: pageSize,
    }));

    getInventoriesSearchResultHandler({ page, pageSize });
  };

  const onOpen = useCallback(
    (id: number) => {
      navigate(`${INVENTORY_PATH}/${id}`);
    },
    [navigate]
  );

  const showOpenInventoryButton = useCallback(
    (inventory: ModelWarehouseInventory) => {
      switch (inventory?.State) {
        case ModelWarehouseInventoryState.Open:
          return IsInventoryCompleting;
        case ModelWarehouseInventoryState.Done:
          return IsInventoryReports;
        default:
          return false;
      }
    },
    [IsInventoryCompleting, IsInventoryReports]
  );

  const onEdit = useCallback(
    (id: number) => {
      navigate(`${EDIT_INVENTORY_PATH}?id=${id}`);
    },
    [navigate]
  );

  const columns = useMemo(
    () =>
      getInventoryColumns({
        onOpen,
        onEdit,
        IsInventoryEdit,
        showOpenInventoryButton,
      }),
    [onOpen, onEdit, IsInventoryEdit, showOpenInventoryButton]
  );
  const warehouseIds = useMemo(() => {
    if (!warehouses) {
      return [];
    }
    return warehouses.map(({ Id }) => Id);
  }, [warehouses]);

  const searchInventoriesHandler = useCallback(async () => {
    try {
      const response = await searchInventories({
        ActiveBranchId,
        data: {
          WarehouseIds: warehouseIds,
          ItemsPerPage: initialFilters.ItemsPerPage,
          PageN: initialFilters.Page,
        },
      }).unwrap();

      setInventoriesSearchResult(response);

      searchIdRef.current = response.Id;
      lastBranchRef.current = ActiveBranchId;
    } catch (error) {
      showNotification('Ошибка при поиске инвентаризаций', 'warning');
    }
  }, [searchInventories, ActiveBranchId, warehouseIds, showNotification]);

  const isPageLoading = useMemo(
    () =>
      isLoadingSearchInventories ||
      isLoadingGetInventoriesSearchResult ||
      isLoadingWarehouses,
    [
      isLoadingSearchInventories,
      isLoadingGetInventoriesSearchResult,
      isLoadingWarehouses,
    ]
  );

  return {
    columns,
    tableData,
    total,
    filters,
    range,
    isPageLoading,
    handlePaginationChange,
    searchInventoriesHandler,
  };
};
